import type { ColumnDef, ColumnMeta, Row, RowData } from '@tanstack/react-table';
import type { ReactNode } from 'react';

/**
 * Extend TanStack Table's ColumnMeta to include application-specific fields.
 * This allows us to attach custom metadata to columns for CSV export, styling, etc.
 */
declare module '@tanstack/react-table' {
  interface ColumnMeta<TData extends RowData, TValue> {
    /** Minimum column width in pixels */
    minWidth: number;
    /** Maximum column width in pixels (columns with maxWidth can expand) */
    maxWidth?: number;

    /** Convert value to string representation (for CSV fallback, print, etc.) */
    toString: (value: TData) => string;
    /** Custom CSV export formatter - returns key-value pairs for multiple CSV columns */
    toCSV?: (item: TData) => { [key: string]: string };

    /** Plain-language column description, surfaced via an info icon in the header meta-row. */
    gloss?: ReactNode;
  }
}

/** Minimal row type for cell rendering — keeps wrapProductColumn constructable */
export type CellRow<TData> = Pick<Row<TData>, 'original' | 'id'>;

/** Minimal table surface needed by cell renderers (visual index lookup) */
export type CellTable = {
  getSortedRowModel: () => { flatRows: ReadonlyArray<{ id: string }> };
};

/** Minimal row type for sorting (subset of TanStack Row) */
type SortRow<TData extends RowData> = Pick<Row<TData>, 'original'>;

export type StrictColumnDef<TData extends RowData> = Omit<
  ColumnDef<TData, string>,
  'accessorFn' | 'cell' | 'sortingFn' | 'meta' | 'header'
> & {
  id: string;
  header: string | (() => ReactNode);
  accessorFn: (value: TData) => string;
  /** Custom cell renderer. Compatible with TanStack CellContext. */
  cell?: (props: { table: CellTable; row: CellRow<TData>; getValue: () => string }) => ReactNode;
  sortingFn?: (rowA: SortRow<TData>, rowB: SortRow<TData>, columnId: string) => number;
  meta: ColumnMeta<TData, string>;
};

/**
 * Configuration for defining a TanStack column with application-specific features.
 * Provides a clean API similar to the legacy ColumnDefinition while returning
 * a native TanStack ColumnDef.
 */
export type ColumnConfig<T> = {
  /** Column identifier and header label */
  id: string;
  /** Minimum column width in pixels */
  minWidth: number;
  /** Maximum column width in pixels (columns with maxWidth can expand) */
  maxWidth?: number;

  /** Convert value to string representation (for CSV fallback, print, etc.) */
  accessorFn: (value: T) => string;

  /** Custom comparison function for sorting */
  sortingFn?: (rowA: T, rowB: T) => number;

  /** Custom cell renderer. Destructure only what you need. */
  cell?: (props: { table: CellTable; row: CellRow<T>; item: T; value: string }) => ReactNode;

  /** Custom CSV export formatter - returns key-value pairs for multiple CSV columns */
  toCSV?: (row: T) => { [key: string]: string };

  /**
   * Plain-language column description. Rendered as an info icon in the header
   * cell's meta-row (see table.tsx) — never inside the label's box, so it can't
   * collide with a wrapping multi-line label.
   */
  gloss?: ReactNode;
};

/**
 * Create a TanStack ColumnDef with application-specific configuration.
 *
 * @example
 * ```tsx
 * const itemCodeColumn = defineColumn<Product>({
 *   id: 'Item Code',
 *   minWidth: 55,
 *   getValue: (product) => product.item_code || product.suggested_item_code,
 *   cell: ({ value }) => <span>{value}</span>,
 * });
 * ```
 */
export function defineColumn<T>(config: ColumnConfig<T>): StrictColumnDef<T> {
  const {
    id,
    minWidth,
    maxWidth,
    accessorFn,
    cell,
    toCSV,
    gloss,
    sortingFn: customSortingFn,
  } = config;

  // Default sorting function if not provided by the user
  const sortingFn =
    customSortingFn ??
    ((rowA: T, rowB: T) => {
      const aValue = accessorFn(rowA);
      const bValue = accessorFn(rowB);

      return aValue.localeCompare(bValue);
    });

  return {
    id,
    header: id,
    accessorFn,

    cell: cell
      ? ({ row, getValue, table }) => {
          return cell({ item: row.original, row, value: getValue(), table });
        }
      : ({ getValue }) => getValue(),

    sortingFn: (rowA, rowB, _columnId) => sortingFn(rowA.original, rowB.original),
    enableSorting: true,

    // Size configuration
    minSize: minWidth,
    maxSize: maxWidth,
    size: minWidth,

    // Store properties in meta for CSV export, print, sizing, etc.
    meta: {
      toString: accessorFn,
      toCSV,
      gloss,
      minWidth,
      maxWidth,
    },
  };
}

/** Type guard to narrow ColumnDef to StrictColumnDef (for extracting columns from TanStack Table API) */
export function isStrictColumnDef<T extends RowData>(
  col: ColumnDef<T, unknown>
): col is ColumnDef<T, unknown> & StrictColumnDef<T> {
  return typeof col.id === 'string' && col.meta !== undefined;
}
